import { isEqual } from 'lodash';
import { AiOutlineCloseCircle } from 'react-icons/ai';
import { useState } from 'react';

import { TOTAL_RECIPE_COUNT } from '../../constants/recipes';
import { MetaPages } from '../../enums/pages';
import { RecipeDropdownTabs } from '../../enums/contentTabs';
import NameOnlyCard from '../../components/atoms/name-only-card/name-only-card';
import { RecipeModel } from '../../interface/inspirations-and-planning';
import DropdownTab from '../../components/atoms/dropdown-tab/dropdown-tab';
import SectionContainer from '../../components/atoms/section-container/section-container';
import SectionDescription from '../../components/atoms/section-description/section-description';
import SectionTitle from '../../components/atoms/section-title/section-title';
import Tabs from '../../components/molecules/tabs/tabs';
import HeaderContainer from '../../components/molecules/header-container/header-container';
import {
  getInspirationsAndPlanningBannerContent,
  getRecipesContent,
  getRecipesCourseDropdownContent,
  getRecipesFoodTypeDropdownContent,
  getRecipesHeaderContent,
  getRecipesProductDropdownContent
} from '../../services/inspirations-and-planning';
import { inspirationsAndPlanningRoutes } from '../../config/routes/pages';
import { ContentType, DropdownType } from '../../types/content';
import TabTitle from '../../components/atoms/tab-title/tab-title';
import { useFilterByRecipes } from '../../hooks/useFilterByRecipes';

type IndexProps = {
  inspirationsAndPlanningBannerContent: ContentType;
  recipesContent: RecipeModel[];
  recipesCourseDropdownContent: DropdownType;
  recipesFoodTypeDropdownContent: DropdownType;
  recipesHeaderContent: ContentType;
  recipesProductDropdownContent: DropdownType;
};

export function Index(props: IndexProps) {
  const {
    inspirationsAndPlanningBannerContent,
    recipesContent,
    recipesCourseDropdownContent,
    recipesFoodTypeDropdownContent,
    recipesHeaderContent,
    recipesProductDropdownContent
  } = props;

  const { description, title } = recipesHeaderContent;

  const { options: courseOptions } = recipesCourseDropdownContent;
  const { options: initialFoodtypeOptions } = recipesFoodTypeDropdownContent;
  const { options: productOptions } = recipesProductDropdownContent;

  const [firstCourseOption] = courseOptions;
  const [firstFoodTypeOption] = initialFoodtypeOptions;
  const [firstProductOption] = productOptions;

  const [currentCourseOption, setCurrentCourseOption] =
    useState(firstCourseOption);
  const [currentFoodTypeOption, setCurrentFoodTypeOption] =
    useState(firstFoodTypeOption);
  const [currentProductOption, setCurrentProductOption] =
    useState(firstProductOption);

  const filteredRecipesContent = recipesContent.filter((recipeItem) => {
    const filteredCourse = isEqual(
      currentCourseOption.tab,
      RecipeDropdownTabs.ALL_COURSES
    )
      ? true
      : recipeItem.attributes.courses.includes(currentCourseOption.value);

    const filteredFoodType = isEqual(
      currentFoodTypeOption.tab,
      RecipeDropdownTabs.ALL_FOODTYPE
    )
      ? true
      : recipeItem.attributes.food_type.includes(currentFoodTypeOption.value);

    const filteredProductOption = isEqual(
      currentProductOption.tab,
      RecipeDropdownTabs.ALL_PRODUCTS
    )
      ? true
      : recipeItem.attributes.category.includes(currentProductOption.value);

    return filteredCourse && filteredFoodType && filteredProductOption;
  });

  const { foodtypeOptions, productsOptions, handleResetFilters } =
    useFilterByRecipes(
      currentCourseOption,
      currentFoodTypeOption,
      currentProductOption,
      filteredRecipesContent,
      firstCourseOption,
      firstFoodTypeOption,
      firstProductOption,
      initialFoodtypeOptions,
      productOptions,
      recipesContent,
      setCurrentCourseOption,
      setCurrentFoodTypeOption,
      setCurrentProductOption
    );

  const cards = filteredRecipesContent.map((cardItemContent) => (
    <NameOnlyCard
      hasClickableName
      hasIconsOnHover
      ctaLink={cardItemContent.attributes.slug}
      imageHeight={57}
      imagePadding="p-2"
      key={cardItemContent.id}
      src={cardItemContent.attributes.recipe_image.data.attributes.url}
      value={cardItemContent.attributes.recipe_name}
    />
  ));

  return (
    <>
      <TabTitle page={MetaPages.RECIPES} />
      <HeaderContainer {...inspirationsAndPlanningBannerContent} />
      <Tabs tabItems={inspirationsAndPlanningRoutes} />
      <SectionContainer>
        <div className="lg:mx-14 mb-4">
          <SectionTitle title={title} />
          <SectionDescription isCentered description={description} />
        </div>
        <div className="w-full mb-4">
          <button
            className="font-MuseoSans100 text-primaryGray text-[13px] flex justify-center items-center m-auto hover:text-primaryBlue"
            onClick={handleResetFilters}
          >
            <AiOutlineCloseCircle className="mr-px" />
            Reset All Filters
          </button>
        </div>
        <div className="flex flex-col sm:flex-row justify-center space-y-2 sm:space-y-0 sm:space-x-2">
          <DropdownTab
            options={courseOptions}
            value={currentCourseOption}
            setTab={setCurrentCourseOption}
          />
          <DropdownTab
            options={foodtypeOptions}
            value={currentFoodTypeOption}
            setTab={setCurrentFoodTypeOption}
          />
          <DropdownTab
            options={productsOptions}
            value={currentProductOption}
            setTab={setCurrentProductOption}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {cards}
        </div>
      </SectionContainer>
    </>
  );
}

export async function getStaticProps() {
  const inspirationsAndPlanningBannerContent =
    await getInspirationsAndPlanningBannerContent();
  const recipesHeaderContent = await getRecipesHeaderContent();
  const recipesCourseDropdownContent = await getRecipesCourseDropdownContent();
  const recipesFoodTypeDropdownContent =
    await getRecipesFoodTypeDropdownContent();
  const recipesProductDropdownContent =
    await getRecipesProductDropdownContent();
  const recipesContent = await getRecipesContent(TOTAL_RECIPE_COUNT);

  return {
    props: {
      inspirationsAndPlanningBannerContent,
      recipesContent,
      recipesCourseDropdownContent,
      recipesFoodTypeDropdownContent,
      recipesHeaderContent,
      recipesProductDropdownContent
    }
  };
}

export default Index;
